import { servedLocaleFor } from '@/i18n/format'
import type { Locale } from '@/i18n/locales'

import { LOCALE_DEFAULT_CURRENCY, type DisplayCurrency } from './map'
import { CURRENCY_PARAM } from './resolve'

/**
 * The address that renders the page a reader is on in `currency`.
 *
 * The locale's own default names the **param-free** URL, because a `cur` equal to it is
 * redundant and redirects — an option offering the reader their default has to name the
 * canonical page directly, or every return to it costs a hop. Every other currency
 * replaces whatever the address carried. The rest of the query survives either way: on
 * the travel review it *is* the page.
 *
 * Distinct from `hrefWithDisplayCurrency`, which carries **the page's own** selection
 * onto a link to somewhere else. This builds a link to the *same* page in a **named**
 * currency, so it sets rather than appends, and it cannot decline.
 */
export function currencyHref(
    pathname: string,
    search: string,
    currency: DisplayCurrency,
    locale: Locale,
): string {
    const params = new URLSearchParams(search)

    if (currency === LOCALE_DEFAULT_CURRENCY[locale]) {
        params.delete(CURRENCY_PARAM)
    } else {
        params.set(CURRENCY_PARAM, currency)
    }

    const query = params.toString()
    return query ? `${pathname}?${query}` : pathname
}

/**
 * A currency's symbol as the active locale writes it — `$` for `USD` on `en-us`, `MX$`
 * for `MXN` there, and the reverse on `es-mx`.
 *
 * Read out of the same ICU data every rendered price is formatted from — resolved on the
 * same served locale (`servedLocaleFor`) — so a control that names a currency and a price
 * stated in it cannot disagree about what it looks like. `Intl` directly rather than the
 * shared formatter only because extracting a symbol is not formatting a number, and that
 * formatter exposes no parts API; it is the same constructor underneath.
 *
 * `formatToParts` rather than trimming digits out of a formatted string: the currency part
 * is the symbol, whatever position, spacing or numbering system the locale puts around it.
 * Falls back to the code, which is what a locale without a symbol renders anyway.
 */
export function currencySymbol(locale: string, currency: DisplayCurrency): string {
    const parts = new Intl.NumberFormat(servedLocaleFor(locale), {
        style: 'currency',
        currency,
    }).formatToParts(0)

    return parts.find((part) => part.type === 'currency')?.value ?? currency
}
